import { App } from 'antd';
import { useCallback, useEffect, useState } from 'react';

import { useSession } from 'technical/auth/client';
import {
  getLocalStorageValue,
  insertOrUpdateLocalStorageValue,
  removeLocalStorageValue,
} from 'technical/local-storage';
import {
  LAST_SYNC_TIMESTAMP_KEY,
  PENDING_UPLOAD_TIMESTAMP_KEY,
  SCAN_SITE_KEY,
} from 'technical/local-storage/constants';
import { scanSiteSchema } from 'technical/local-storage/types';
import { z } from 'zod';
import { segmentSyncService } from 'technical/db/sync/segment-sync.service';
import { ringSyncService } from 'technical/db/sync/ring-sync.service';
import { useTranslation } from 'translations/hooks';

import { drawConfigSyncService } from 'technical/db/sync/draw-config-sync.service';
import { useDataFetch } from './use-data-fetch';
import { useSegmentV1ControllerUpload } from 'generated/apiComponents';
import { type Segment } from 'technical/db';
import { utilsDbService } from 'technical/db/db.service';
import dayjs from 'dayjs';

const useUpload = () => {
  const { t } = useTranslation();
  const { data: session } = useSession();
  const { message } = App.useApp();
  const [isUploading, setIsUploading] = useState(false);

  const scanSite = getLocalStorageValue({
    key: SCAN_SITE_KEY,
    schema: scanSiteSchema,
  });

  const { mutateAsync } = useSegmentV1ControllerUpload();
  const uploadSegments = async (segments: Segment[]) => {
    return mutateAsync({
      body: {
        projectId: session?.user.projectId ?? 'no-project-id',
        scanSiteId: scanSite?.id ?? 'no-scan-site',
        segments: segments.map((segment) => ({
          ...segment,
          ringId: segment.ringId ?? undefined,
        })),
      },
    });
  };

  const upload = async () => {
    const projectId = session?.user.projectId;
    if (!projectId) {
      return { success: false };
    }

    insertOrUpdateLocalStorageValue({
      key: PENDING_UPLOAD_TIMESTAMP_KEY,
      value: dayjs().toISOString(),
    });
    setIsUploading(true);
    try {
      if (scanSite?.id) {
        await segmentSyncService.upload(uploadSegments);
      }

      removeLocalStorageValue({ key: PENDING_UPLOAD_TIMESTAMP_KEY });
      return { success: true };
    } catch (error) {
      message.error(t('common.technicalError'));
      return { success: false, error };
    } finally {
      setIsUploading(false);
    }
  };

  return { isUploading, upload };
};

const useDownload = () => {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const { data: session } = useSession();

  const [isFetchEnabled, setIsFetchEnabled] = useState(false);
  const [updatedSince, setUpdatedSince] = useState<string | undefined>(
    undefined,
  );

  const {
    segments,
    isSegmentsSuccess,
    isSegmentsFetching,
    rings,
    isRingsSuccess,
    isRingsFetching,
    segmentDrawConfig,
    isSegmentDrawConfigSuccess,
    isSegmentDrawConfigFetching,
  } = useDataFetch({
    enabled: isFetchEnabled,
    updatedSince,
  });

  const isDownloading =
    isSegmentsFetching || isRingsFetching || isSegmentDrawConfigFetching;

  useEffect(() => {
    if (
      !isFetchEnabled ||
      !isSegmentsSuccess ||
      !isRingsSuccess ||
      !isSegmentDrawConfigSuccess
    ) {
      return;
    }

    const saveData = async () => {
      try {
        await segmentSyncService.download(segments?.segments ?? []);
        await ringSyncService.download(rings?.rings ?? []);
        await drawConfigSyncService.download(
          segmentDrawConfig?.drawConfigs ?? [],
        );
        insertOrUpdateLocalStorageValue({
          key: LAST_SYNC_TIMESTAMP_KEY,
          value: dayjs().toISOString(),
        });
      } catch {
        message.error(t('common.technicalError'));
      } finally {
        setIsFetchEnabled(false);
      }
    };

    saveData();
  }, [
    isFetchEnabled,
    isSegmentsSuccess,
    isRingsSuccess,
    isSegmentDrawConfigSuccess,
    segments?.segments,
    rings?.rings,
    segmentDrawConfig?.drawConfigs,
    message,
    t,
    updatedSince,
  ]);

  const download = useCallback(async () => {
    if (!session?.user.projectId) {
      return { success: false };
    }

    const lastSyncTimestamp = (await utilsDbService.isDbEmpty())
      ? undefined
      : getLocalStorageValue({
          key: LAST_SYNC_TIMESTAMP_KEY,
          schema: z.string(),
        });
    const updatedSinceWithDelay = lastSyncTimestamp
      ? dayjs(lastSyncTimestamp).subtract(5, 'minute').toISOString()
      : undefined;
    setUpdatedSince(updatedSinceWithDelay);

    setIsFetchEnabled(true);

    return { success: true };
  }, [session?.user.projectId]);

  return {
    isDownloading,
    download,
  };
};

export const useSyncData = () => {
  const { isUploading, upload } = useUpload();
  const { isDownloading, download } = useDownload();

  return { upload, isUploading, download, isDownloading };
};
